import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Portal } from "../../components/Portal";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { useTranslation } from "react-i18next";

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

async function getCroppedImgAndFile(imageSrc, pixelCrop, fileName = "cropped.jpg", rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );
  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Canvas is empty"));
        return;
      }
      const file = new File([blob], fileName, { type: "image/jpeg" });
      resolve({ url: URL.createObjectURL(blob), file });
    }, "image/jpeg");
  });
}

const PostImageCropperModal = ({ imagesToCrop, onComplete, onCancel }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  
  // Accumulated cropped images
  const [croppedImages, setCroppedImages] = useState([]);

  const currentImage = imagesToCrop[currentIndex];

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleApplyCrop = async () => {
    if (!currentImage || !croppedAreaPixels) return;

    try {
      const { url, file } = await getCroppedImgAndFile(
        currentImage.url,
        croppedAreaPixels,
        currentImage.name
      );

      const newCroppedImages = [
        ...croppedImages,
        { name: currentImage.name, url, file },
      ];

      if (currentIndex < imagesToCrop.length - 1) {
        setCroppedImages(newCroppedImages);
        setCurrentIndex((prev) => prev + 1);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      } else {
        // Finished all images
        onComplete(newCroppedImages);
      }
    } catch (e) {
      console.error(e);
      // Fallback: just use original if crop fails
      const newCroppedImages = [...croppedImages, currentImage];
      if (currentIndex < imagesToCrop.length - 1) {
        setCroppedImages(newCroppedImages);
        setCurrentIndex((prev) => prev + 1);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      } else {
        onComplete(newCroppedImages);
      }
    }
  };

  if (!currentImage) return null;

  return (
    <Portal layer="modal">
      <div
        className="modal fade show wlj-overlay-modal"
        tabIndex="-1"
        style={{ display: "flex", background: "rgba(0,0,0,0.8)" }}
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "600px", width: "100%" }}>
          <div className="modal-content" style={{ overflow: "hidden" }}>
            <div className="modalBorder d-flex justify-content-between align-items-center p-3">
              <h6 className="m-0 fw-bold">
                Crop Image {currentIndex + 1} of {imagesToCrop.length}
              </h6>
              <CloseSharpIcon className="pointer" onClick={onCancel} />
            </div>

            <div style={{ position: "relative", width: "100%", height: "400px", background: "#333" }}>
              <Cropper
                image={currentImage.url}
                crop={crop}
                zoom={zoom}
                aspect={4 / 4}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>

            <div className="p-3">
              <div className="d-flex align-items-center mb-3">
                <label className="me-3 mb-0 fw-bold">Zoom</label>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  onChange={(e) => setZoom(e.target.value)}
                  className="form-range w-100"
                />
              </div>
              
              <div className="d-flex justify-content-end gap-2">
                <button type="button" className="wljDangerBtn" onClick={onCancel}>
                  Cancel
                </button>
                <button type="button" className="wljPrimaryBtn" onClick={handleApplyCrop}>
                  {currentIndex < imagesToCrop.length - 1 ? "Next" : "Apply & Post"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default PostImageCropperModal;
